import { CloseCircleFilled, WarningFilled } from "@ant-design/icons"
import { Button, Flex, Modal, Typography } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { showErrorNotifications } from "../../../utils/handlers/handle-error"
import { contractValidationHasIssues } from "./calculate-contract-validation.ts"
import { formatSalesPriceLine, formatScheduleLine, type ValidationLine } from "./format-contract-validation.ts"
import type { ContractValidationResult } from "./types.ts"

const { Text, Title } = Typography

interface ContractValidationConfirmModalProps {
  open: boolean
  /** Go Back & Fix — closes the modal only. The Contract's own edits were
   * already saved by the PATCH that runs before this modal opens (same
   * convention as `ContractGenerateConfirmModal`), so nothing here needs to
   * undo anything — the user just lands back in the editor. */
  onClose: () => void
  result: ContractValidationResult | null
  /** Generate Anyway — proceeds through the EXISTING generate flow
   * unchanged (the same sync-check step that runs when there are no
   * validation issues at all). This modal never alters that flow, only
   * gates entry to it. */
  onProceed: () => void | Promise<void>
}

/**
 * Shown only when a Generate-time recheck (the authoritative backend
 * `GET .../validation/` call, against the just-saved data) finds Sales Price
 * or the combined Schedule category (Project Duration, Milestone Duration,
 * Total Milestone Duration, Milestone Dates — see `formatScheduleLine`)
 * doesn't add up — a plain-language guardrail, never a blocker: "Generate
 * Anyway" always remains available. Renders one block per non-`valid`,
 * non-`incomplete` category (a `warning` or `error`) — an `incomplete`
 * category never opens this modal at all (see `contractValidationHasIssues`),
 * so it never renders a block here either.
 */
function ContractValidationConfirmModal({
  open,
  onClose,
  result,
  onProceed
}: ContractValidationConfirmModalProps) {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)

  if (!result || !contractValidationHasIssues(result)) return null

  const salesPriceLine = formatSalesPriceLine(t, result.sales_price)
  const scheduleLine = formatScheduleLine(t, result)

  // Only a `warning`/`error` category ever carries a `comparison` in the
  // first place (see `formatSalesPriceLine`/`formatScheduleLine`) — the
  // explicit status check here is the same defensive floor
  // `contractValidationHasIssues` above already is, kept because TypeScript
  // can't narrow `line.comparison` truthy back into "status is warning or
  // error" on its own.
  const lines: Array<ValidationLine & { status: "warning" | "error" }> = []
  if (
    salesPriceLine?.comparison &&
    (result.sales_price.status === "warning" || result.sales_price.status === "error")
  ) {
    lines.push({ ...salesPriceLine, status: result.sales_price.status })
  }
  if (scheduleLine?.comparison && (scheduleLine.status === "warning" || scheduleLine.status === "error")) {
    lines.push({ ...scheduleLine, status: scheduleLine.status })
  }

  const handleGenerateAnyway = async () => {
    try {
      setSaving(true)
      await onProceed()
      onClose()
    } catch (e) {
      showErrorNotifications(e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onCancel={saving ? undefined : onClose}
      maskClosable={false}
      centered
      width={560}
      data-testid={"contract-validation-confirm-modal"}
      title={
        <Title level={4} style={{ margin: 0 }}>
          {t("contractValidation.reviewModal.title")}
        </Title>
      }
      footer={[
        <Button key={"goBack"} disabled={saving} onClick={onClose}>
          {t("contractValidation.reviewModal.goBack")}
        </Button>,
        <Button
          key={"generateAnyway"}
          type={"primary"}
          loading={saving}
          onClick={handleGenerateAnyway}
        >
          {t("contractValidation.reviewModal.generateAnyway")}
        </Button>
      ]}
    >
      <Text>
        {t("contractValidation.reviewModal.summary", { count: lines.length })}
      </Text>

      <Flex vertical gap={12} className={"mt-4"}>
        {lines.map((line, index) => (
          <Flex
            key={index}
            gap={8}
            align={"flex-start"}
            data-testid={"contract-validation-confirm-line"}
          >
            {line.status === "error" ? (
              <CloseCircleFilled style={{ color: "#ff4d4f", marginTop: 2 }} />
            ) : (
              <WarningFilled style={{ color: "#faad14", marginTop: 2 }} />
            )}
            <Flex vertical gap={4}>
              <Text strong>{line.title}</Text>
              {(Array.isArray(line.comparison) ? line.comparison : [line.comparison]).map(
                (comparison, comparisonIndex) => (
                  <Text key={comparisonIndex} type={"secondary"}>
                    {comparison}
                  </Text>
                )
              )}
            </Flex>
          </Flex>
        ))}
      </Flex>
    </Modal>
  )
}

export { ContractValidationConfirmModal }
